import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import PageShell from "@/components/PageShell";
import DataDisciplineNote from "@/components/DataDisciplineNote";
import { Compass, Sparkles, Target, Loader2, RefreshCw, PackagePlus } from "lucide-react";

const SPECIALTY_OPTIONS = [
  ["primary_care", "Primary care"],
  ["concierge", "Concierge / membership"],
  ["functional", "Functional medicine"],
  ["wellness_clinic", "Wellness clinic"],
  ["med_spa", "Med spa / aesthetics"],
  ["weight_loss", "Weight-management clinic"],
  ["longevity_clinic", "Longevity / preventive"],
  ["sleep_clinic", "Sleep clinic"],
  ["sports_performance", "Sports / performance"],
  ["other", "Other"],
];

const SIZE_OPTIONS = [
  ["solo", "Solo (1 provider)"],
  ["small", "Small (2-5)"],
  ["medium", "Medium (6-15)"],
  ["large", "Large (16-50)"],
  ["enterprise", "Enterprise (50+)"],
];

const LOCATION_OPTIONS = [
  ["urban", "Urban"],
  ["suburban", "Suburban"],
  ["rural", "Rural"],
];

const DEMO_OPTIONS = [
  ["young_adult", "Young adult"],
  ["professional", "Working professional"],
  ["perimenopausal", "Perimenopausal / midlife women"],
  ["executive", "High-income executive"],
  ["retiree", "Retiree"],
  ["athletic", "Athletic / performance"],
  ["mixed", "Mixed / general"],
];

const CATEGORY_OPTIONS = [
  ["sleep", "Sleep"],
  ["metabolic", "Metabolic"],
  ["hormone", "Hormone"],
  ["diagnostics", "Diagnostics"],
  ["wellness", "Wellness"],
  ["longevity", "Longevity"],
  ["recovery", "Recovery"],
];

const EMPTY = {
  specialty: "concierge",
  clinic_size: "medium",
  location_type: "suburban",
  region: "",
  current_services: [],
  demographic_profile: ["mixed"],
  target_categories: [],
};

const inputCls =
  "block w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-slate-900 focus:ring-1 focus:ring-slate-900";

const HEAT_BG = (s) => {
  if (s >= 80) return "from-emerald-500 to-emerald-600 text-white";
  if (s >= 65) return "from-blue-500 to-blue-600 text-white";
  if (s >= 50) return "from-amber-400 to-amber-500 text-amber-900";
  return "from-slate-300 to-slate-400 text-slate-800";
};

function Toggle({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition ${
        active
          ? "bg-slate-900 text-white border border-slate-900"
          : "bg-white text-slate-700 border border-slate-200 hover:bg-slate-50"
      }`}
    >
      {children}
    </button>
  );
}

export default function PortalOpportunityScanner() {
  const [form, setForm] = useState(EMPTY);
  const [serviceInput, setServiceInput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Load latest scan if one exists. Skipped when the URL carries `?fresh=1`
  // — that's how the auto-rendered demo video walks the explainer scene
  // without the previous scan's cached results flashing into view first.
  useEffect(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      if (params.get("fresh") === "1") return;
    }
    api.get("/clinic/opportunity-scan/latest")
      .then((r) => { if (r.data && r.data.id) setResult(r.data); })
      .catch(() => {});
  }, []);

  const toggleArr = (key, val) => {
    setForm((f) => ({
      ...f,
      [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val],
    }));
  };

  const addService = () => {
    const v = serviceInput.trim();
    if (!v) return;
    setForm((f) => ({ ...f, current_services: Array.from(new Set([...f.current_services, v])) }));
    setServiceInput("");
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await api.post("/clinic/opportunity-scan", form);
      setResult(r.data);
      toast.success("Opportunity scan complete.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => { setResult(null); setForm(EMPTY); };

  return (
    <PageShell
      eyebrow="Cash-Pay Opportunity Scanner"
      title="Identify the highest-impact revenue opportunities for your clinic."
      intro="Business intelligence and operational opportunity analysis. Scores are based on specialty alignment, demographic match, market demand, recurring potential, operational complexity, and vendor coverage in our network."
      testId="opportunity-scanner-page"
      actions={
        result ? (
          <button
            onClick={reset}
            data-testid="scanner-reset"
            className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 rounded-full px-4 py-2 text-sm font-semibold hover:bg-slate-50"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Run a new scan
          </button>
        ) : null
      }
    >
      {/* Disclaimer banner — operational only */}
      <div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 mb-5 text-xs text-blue-900 leading-relaxed flex gap-2">
        <Sparkles className="w-4 h-4 mt-0.5 shrink-0" />
        <div>
          <b>Operational intelligence only.</b> This scanner surfaces business and market signals — it does not provide
          clinical recommendations, medical advice, or treatment guidance. Clinics retain full responsibility for clinical decisions.
        </div>
      </div>

      {!result ? (
        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="scanner-form">
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-4">
              Tell us about your clinic
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Clinic specialty</label>
                <select
                  data-testid="scanner-specialty"
                  className={inputCls}
                  value={form.specialty}
                  onChange={(e) => setForm({ ...form, specialty: e.target.value })}
                >
                  {SPECIALTY_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Clinic size</label>
                <select
                  data-testid="scanner-size"
                  className={inputCls}
                  value={form.clinic_size}
                  onChange={(e) => setForm({ ...form, clinic_size: e.target.value })}
                >
                  {SIZE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Location type</label>
                <select
                  data-testid="scanner-location-type"
                  className={inputCls}
                  value={form.location_type}
                  onChange={(e) => setForm({ ...form, location_type: e.target.value })}
                >
                  {LOCATION_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Region (optional)</label>
                <input
                  data-testid="scanner-region"
                  className={inputCls}
                  placeholder="e.g. Pacific Northwest, NYC metro…"
                  value={form.region}
                  onChange={(e) => setForm({ ...form, region: e.target.value })}
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">Patient demographic profile</label>
              <div className="flex flex-wrap gap-1.5">
                {DEMO_OPTIONS.map(([v, l]) => (
                  <Toggle
                    key={v}
                    active={form.demographic_profile.includes(v)}
                    onClick={() => toggleArr("demographic_profile", v)}
                    testId={`scanner-demo-${v}`}
                  >
                    {l}
                  </Toggle>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                Current services offered <span className="text-[10px] text-slate-400 font-normal">(optional)</span>
              </label>
              <div className="flex gap-2">
                <input
                  data-testid="scanner-service-input"
                  className={inputCls}
                  placeholder="e.g. preventive labs, weight management"
                  value={serviceInput}
                  onChange={(e) => setServiceInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addService(); } }}
                />
                <button
                  type="button"
                  onClick={addService}
                  className="bg-slate-900 text-white rounded-lg px-3 py-2 text-xs font-semibold hover:bg-slate-800"
                >
                  Add
                </button>
              </div>
              {form.current_services.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {form.current_services.map((s) => (
                    <span key={s} className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-700 px-2.5 py-1 text-[11px]">
                      {s}
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, current_services: form.current_services.filter((x) => x !== s) })}
                        className="text-slate-400 hover:text-slate-700"
                      >×</button>
                    </span>
                  ))}
                </div>
              )}
              <DataDisciplineNote className="mt-2" />
            </div>

            <div className="mb-5">
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">Categories you're targeting</label>
              <div className="flex flex-wrap gap-1.5">
                {CATEGORY_OPTIONS.map(([v, l]) => (
                  <Toggle
                    key={v}
                    active={form.target_categories.includes(v)}
                    onClick={() => toggleArr("target_categories", v)}
                    testId={`scanner-cat-${v}`}
                  >
                    {l}
                  </Toggle>
                ))}
              </div>
            </div>

            <button
              onClick={submit}
              disabled={submitting}
              data-testid="scanner-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-slate-900 text-white rounded-full px-4 py-2.5 text-sm font-semibold hover:bg-slate-800 disabled:opacity-60"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Compass className="w-4 h-4" />}
              {submitting ? "Scanning…" : "Run opportunity scan"}
            </button>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-900 to-slate-800 text-white p-6 self-start">
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-blue-300 mb-3">
              How scoring works
            </div>
            <h3 className="font-display text-lg font-bold leading-snug mb-3">
              Each cash-pay category is scored across 7 operational and market signals.
            </h3>
            <ul className="text-xs space-y-2 text-slate-200 leading-relaxed">
              <li className="flex gap-2"><Target className="w-3.5 h-3.5 mt-0.5 text-blue-300 shrink-0" /> Specialty alignment</li>
              <li className="flex gap-2"><Target className="w-3.5 h-3.5 mt-0.5 text-blue-300 shrink-0" /> Demographic match</li>
              <li className="flex gap-2"><Target className="w-3.5 h-3.5 mt-0.5 text-blue-300 shrink-0" /> Market demand index</li>
              <li className="flex gap-2"><Target className="w-3.5 h-3.5 mt-0.5 text-blue-300 shrink-0" /> Recurring revenue potential</li>
              <li className="flex gap-2"><Target className="w-3.5 h-3.5 mt-0.5 text-blue-300 shrink-0" /> Operational simplicity</li>
              <li className="flex gap-2"><Target className="w-3.5 h-3.5 mt-0.5 text-blue-300 shrink-0" /> Vendor availability in our network</li>
              <li className="flex gap-2"><Target className="w-3.5 h-3.5 mt-0.5 text-blue-300 shrink-0" /> Competitive pressure</li>
            </ul>
            <div className="mt-4 text-[11px] text-slate-400 leading-relaxed">
              Output is operational and market intelligence only — not clinical guidance.
            </div>
          </div>
        </div>
      ) : (
        <ScanResult result={result} />
      )}
    </PageShell>
  );
}

function ScanResult({ result }) {
  const top = result.scores?.[0];
  return (
    <div data-testid="scanner-result" className="space-y-5">
      <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 text-white p-6">
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-blue-300 mb-2">
          Operational readiness
        </div>
        <div className="flex items-end gap-3">
          <div className="font-display text-5xl font-extrabold tracking-tight" data-testid="readiness-score">
            {result.overall_readiness_score}
          </div>
          <div className="text-sm text-slate-300 pb-2">/ 100</div>
        </div>
        <p className="text-sm text-slate-300 mt-2 max-w-2xl leading-relaxed">
          Combined signal across your specialty, top-3 category fit, and current operational breadth.
          Higher scores = stronger setup for deploying a structured cash-pay program.
        </p>
        {top && (
          <div className="mt-4 inline-flex items-center gap-2 bg-white/10 rounded-full px-3 py-1.5 text-xs">
            🥇 Top opportunity: <b>{top.label}</b> · score {top.opportunity_score}/100
          </div>
        )}
      </div>

      {/* Heat map row */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-3">
          Opportunity heat map
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
          {result.scores.map((s) => (
            <div
              key={s.category}
              data-testid={`heat-${s.category}`}
              className={`rounded-xl bg-gradient-to-br ${HEAT_BG(s.opportunity_score)} p-3 text-center shadow-sm`}
            >
              <div className="text-[10px] uppercase font-semibold opacity-90 truncate">{s.label}</div>
              <div className="font-display text-2xl font-extrabold mt-1">{s.opportunity_score}</div>
              <div className="text-[10px] opacity-80">opportunity</div>
            </div>
          ))}
        </div>
      </div>

      {/* Recommended Next Programs */}
      <div className="grid md:grid-cols-3 gap-4">
        {result.scores.slice(0, 3).map((s, i) => (
          <ScoreCard key={s.category} score={s} rank={i + 1} />
        ))}
      </div>

      {/* Full breakdown table */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-3">
          Full category breakdown
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-3">Category</th>
                <th className="py-2 px-3">Score</th>
                <th className="py-2 px-3">Demand</th>
                <th className="py-2 px-3">Recurring</th>
                <th className="py-2 px-3">Complexity</th>
                <th className="py-2 px-3">Vendors</th>
                <th className="py-2 px-3">Pressure</th>
                <th className="py-2 px-3">Leakage</th>
              </tr>
            </thead>
            <tbody>
              {result.scores.map((s) => (
                <tr key={s.category} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-2 pr-3 font-semibold text-slate-900">{s.label}</td>
                  <td className="py-2 px-3 font-mono text-slate-900">{s.opportunity_score}</td>
                  <td className="py-2 px-3"><Pill v={s.market_demand} /></td>
                  <td className="py-2 px-3"><Pill v={s.recurring_potential} /></td>
                  <td className="py-2 px-3"><Pill v={s.complexity} invert /></td>
                  <td className="py-2 px-3 text-slate-700">{s.vendor_count}</td>
                  <td className="py-2 px-3"><Pill v={s.competitive_pressure} /></td>
                  <td className="py-2 px-3"><Pill v={s.leakage_risk} invert /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-2">
          Suggested next actions
        </div>
        <ul className="space-y-2 text-sm text-slate-700">
          {result.next_actions?.map((a, i) => (
            <li key={i} className="flex gap-2">
              <span className="text-blue-600 font-bold">{i + 1}.</span>
              <span>{a}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function ScoreCard({ score, rank }) {
  const medal = ["🥇", "🥈", "🥉"][rank - 1] || "•";
  const nav = useNavigate();
  const [building, setBuilding] = useState(false);
  const buildProgram = async () => {
    setBuilding(true);
    try {
      const r = await api.post(`/clinic/programs/from-category/${score.category}`);
      toast.success(`Draft created · "${r.data.name}"`);
      nav(`/portal/programs/${r.data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBuilding(false); }
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 hover:shadow-md transition flex flex-col" data-testid={`scorecard-${score.category}`}>
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500">
            {medal} Recommended next program
          </div>
          <div className="font-display text-lg font-bold text-slate-900 mt-0.5">{score.label}</div>
        </div>
        <div className={`rounded-full px-3 py-1 text-xs font-bold bg-gradient-to-br ${HEAT_BG(score.opportunity_score)}`}>
          {score.opportunity_score}
        </div>
      </div>
      <ul className="text-xs text-slate-700 space-y-1.5 mt-3">
        {score.recommended_programs.map((p, i) => (
          <li key={i} className="flex gap-1.5"><span className="text-blue-600">▸</span><span>{p}</span></li>
        ))}
      </ul>
      <div className="grid grid-cols-3 gap-1 mt-4 text-[10px]">
        <div className="bg-slate-50 rounded p-1.5 text-center">
          <div className="font-semibold text-slate-500">Demand</div>
          <div className="capitalize font-bold">{score.market_demand}</div>
        </div>
        <div className="bg-slate-50 rounded p-1.5 text-center">
          <div className="font-semibold text-slate-500">Recurring</div>
          <div className="capitalize font-bold">{score.recurring_potential}</div>
        </div>
        <div className="bg-slate-50 rounded p-1.5 text-center">
          <div className="font-semibold text-slate-500">Vendors</div>
          <div className="font-bold">{score.vendor_count}</div>
        </div>
      </div>
      <button
        onClick={buildProgram}
        disabled={building}
        data-testid={`build-program-${score.category}`}
        className="mt-4 inline-flex items-center justify-center gap-1.5 bg-slate-900 text-white rounded-full px-3 py-2 text-xs font-semibold hover:bg-slate-800 disabled:opacity-60"
      >
        {building ? <Loader2 className="w-3 h-3 animate-spin" /> : <PackagePlus className="w-3 h-3" />}
        {building ? "Drafting…" : "Build this program"}
      </button>
    </div>
  );
}

function Pill({ v, invert }) {
  if (!v) return <span className="text-slate-400 text-xs">—</span>;
  // when invert, "low" complexity/leakage = good (green); when not invert, "high" = good (green)
  const goodKey = invert ? "low" : "high";
  const badKey = invert ? "high" : "low";
  const cls =
    v === goodKey ? "bg-emerald-100 text-emerald-800"
    : v === badKey ? "bg-rose-100 text-rose-800"
    : "bg-amber-100 text-amber-800";
  return <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] uppercase font-bold ${cls}`}>{v}</span>;
}
